import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { catchError, Observable, of, ReplaySubject, tap } from 'rxjs';

import { Crosstab, CrosstabForm, CrosstabGenerationResponse, CrosstabSaved, ICrosstabTable } from '@empathy/global/crosstabs/models';

import { EnvironmentConfig } from '../config/environment.config';

@Injectable({
  providedIn: 'root'
})
export class CrosstabsService {
  private tablesCache = new Map<string, CrosstabGenerationResponse>();

  constructor(
    @Inject('environment') private environment: EnvironmentConfig,
    private http: HttpClient
  ) {}

  get(projectId: string, researchMethodId: string, crosstabId: string): Observable<CrosstabSaved> {
    return this.http
      .get<CrosstabSaved>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/cross-tabs/${crosstabId}`)
      .pipe(catchError(() => of(null)));
  }

  save(projectId: string, researchMethodId: string, crosstab: Crosstab): Observable<CrosstabSaved> {
    return this.http
      .post<CrosstabSaved>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/cross-tabs`, crosstab)
      .pipe(catchError(() => of(null)));
  }

  updateCrosstab(projectId: string, researchMethodId: string, crossTab: Crosstab, crossTabId: string): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/cross-tabs/${crossTabId}`, crossTab).pipe(
      tap((updated) => {
        if (updated) {
          this.invalidateTablesCache(projectId, researchMethodId, crossTabId);
        }
      }),
      catchError(() => of(false))
    );
  }

  deleteCrosstab(projectId: string, researchMethodId: string, crossTabId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/cross-tabs/${crossTabId}`).pipe(
      tap((deleted) => {
        if (deleted) {
          this.invalidateTablesCache(projectId, researchMethodId, crossTabId);
        }
      }),
      catchError(() => of(false))
    );
  }

  getTables(projectId: string, researchMethodId: string, crosstabId: string, refresh: boolean = false): Observable<CrosstabGenerationResponse> {
    const cacheKey = this.getSavedTablesCacheKey(projectId, researchMethodId, crosstabId);
    if (!refresh && this.tablesCache.has(cacheKey)) {
      return of(this.tablesCache.get(cacheKey) as CrosstabGenerationResponse);
    }

    return this.http
      .get<CrosstabGenerationResponse>(`${this.environment.surveyEndPoint}analytics/crosstabs/${projectId}/${researchMethodId}/${crosstabId}`)
      .pipe(
        tap((response) => this.decorateTables(response.tables)),
        tap((response) => this.tablesCache.set(cacheKey, response)),
        catchError(() => of(this.createEmptyResponse()))
      );
  }

  getTablesFromSaved(
    projectId: string,
    researchMethodId: string,
    crosstabSaved: CrosstabSaved,
    refresh: boolean = false
  ): Observable<CrosstabGenerationResponse> {
    if (!crosstabSaved?.details) {
      return of(this.createEmptyResponse());
    }

    const cacheKey = this.getPreviewTablesCacheKey(projectId, researchMethodId, crosstabSaved.id ?? 'local');
    if (!refresh && this.tablesCache.has(cacheKey)) {
      return of(this.tablesCache.get(cacheKey) as CrosstabGenerationResponse);
    }

    let crosstabForm: CrosstabForm;
    try {
      crosstabForm = JSON.parse(crosstabSaved.details) as CrosstabForm;
    } catch {
      return of(this.createEmptyResponse());
    }

    return this.http
      .post<CrosstabGenerationResponse>(`${this.environment.surveyEndPoint}analytics/crosstabs/${projectId}/${researchMethodId}`, crosstabForm)
      .pipe(
        tap((response) => this.decorateTables(response.tables)),
        tap((response) => this.tablesCache.set(cacheKey, response)),
        catchError(() => of(this.createEmptyResponse(crosstabForm)))
      );
  }

  private createEmptyResponse(crosstabUsed?: CrosstabForm): CrosstabGenerationResponse {
    return {
      tables: [],
      changedLog: [],
      crosstabUsed: (crosstabUsed ?? null) as unknown as CrosstabForm
    };
  }

  private getPreviewTablesCacheKey(projectId: string, researchMethodId: string, crosstabId: string): string {
    return `preview:${projectId}_${researchMethodId}_${crosstabId}`;
  }

  private getSavedTablesCacheKey(projectId: string, researchMethodId: string, crosstabId: string): string {
    return `saved:${projectId}_${researchMethodId}_${crosstabId}`;
  }

  private invalidateTablesCache(projectId: string, researchMethodId: string, crosstabId: string): void {
    this.tablesCache.delete(this.getSavedTablesCacheKey(projectId, researchMethodId, crosstabId));
    this.tablesCache.delete(this.getPreviewTablesCacheKey(projectId, researchMethodId, crosstabId));
  }

  private decorateTables(tables: ICrosstabTable[]): void {
    tables.forEach((table) => this.ensureExportSignal(table));
  }

  private ensureExportSignal(table: any): void {
    if (!table.exporting$) {
      table.exporting$ = new ReplaySubject<boolean>();
    }

    if (Array.isArray(table.tables)) {
      table.tables.forEach((innerTable) => this.ensureExportSignal(innerTable));
    }
  }
}
